import React, { useState, useRef, useEffect } from 'react';
import './ProfileDialog.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { setRecognitionType, setUser } from '../../../../userSlice';
import { useI18n } from '../../../contexts/I18nContext';
import { uploadAvatar, deleteAvatar, getCurrentUser, getCachedApiUrl, getSpecialtyProfessions, updateCurrentUserProfile } from '../../../utils/apiUtils';
import { useSnackbar } from 'notistack';
import CustomSelect from '../../../components/CustomSelect';

interface ProfileDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onLogout: () => void;
}

const ProfileDialog: React.FC<ProfileDialogProps> = ({ isOpen, onClose, onLogout }): React.ReactElement | null => {
    const user = useSelector((state: RootState) => state.user);
    const dispatch = useDispatch();
    const currentType = user?.recognitionType || 'qwen';
    const { t, language, setLanguage } = useI18n();
    const { enqueueSnackbar } = useSnackbar();

    const [isLoadingAvatar, setIsLoadingAvatar] = useState(false);
    const [avatarError, setAvatarError] = useState(false);
    const [professions, setProfessions] = useState<string[]>([]);
    const [loadingProfessions, setLoadingProfessions] = useState(false);
    const [updatingProfession, setUpdatingProfession] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            setLoadingProfessions(true);
            getSpecialtyProfessions()
                .then(setProfessions)
                .catch(() => setProfessions([]))
                .finally(() => setLoadingProfessions(false));
        }
    }, [isOpen]);

    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value === 'qwen' ? 'qwen' : 'azure';
        dispatch(setRecognitionType(value));
    };

    // Получаем полный URL аватара из данных пользователя
    const getAvatarFullUrl = () => {
        if (!user?.avatar_url) return null;
        // avatar_url уже содержит путь типа "/avatars/user_60_f74acdfd.webp"
        return `${getCachedApiUrl()}${user.avatar_url}`;
    };

    const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Проверка типа файла
        if (!file.type.startsWith('image/')) {
            enqueueSnackbar(t('profile.avatar.error.invalidType') || 'Пожалуйста, выберите изображение', { variant: 'error' });
            return;
        }

        // Проверка размера файла (макс 5MB)
        if (file.size > 5 * 1024 * 1024) {
            enqueueSnackbar(t('profile.avatar.error.tooLarge') || 'Размер файла не должен превышать 5MB', { variant: 'error' });
            return;
        }

        setIsLoadingAvatar(true);

        try {
            const response = await uploadAvatar(file);

            if (response.ok) {
                enqueueSnackbar(t('profile.avatar.uploadSuccess') || 'Аватар успешно загружен', { variant: 'success' });

                // Обновляем данные пользователя из API чтобы получить новый avatar_url
                const updatedUser = await getCurrentUser();
                if (updatedUser) {
                    dispatch(setUser(updatedUser));
                }

                setAvatarError(false);
            } else {
                const errorData = await response.json().catch(() => ({}));
                enqueueSnackbar(
                    errorData.detail || t('profile.avatar.error.upload') || 'Ошибка загрузки аватара',
                    { variant: 'error' }
                );
            }
        } catch (error) {
            console.error('Error uploading avatar:', error);
            enqueueSnackbar(t('profile.avatar.error.upload') || 'Ошибка загрузки аватара', { variant: 'error' });
        } finally {
            setIsLoadingAvatar(false);
            // Сбрасываем input для возможности повторной загрузки того же файла
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleAvatarDelete = async () => {
        if (!window.confirm(t('profile.avatar.confirmDelete') || 'Вы уверены, что хотите удалить аватар?')) {
            return;
        }

        setIsLoadingAvatar(true);

        try {
            const response = await deleteAvatar();

            if (response.ok) {
                enqueueSnackbar(t('profile.avatar.deleteSuccess') || 'Аватар успешно удалён', { variant: 'success' });

                // Обновляем данные пользователя из API чтобы avatar_url стал null
                const updatedUser = await getCurrentUser();
                if (updatedUser) {
                    dispatch(setUser(updatedUser));
                }

                setAvatarError(false);
            } else {
                const errorData = await response.json().catch(() => ({}));
                enqueueSnackbar(
                    errorData.detail || t('profile.avatar.error.delete') || 'Ошибка удаления аватара',
                    { variant: 'error' }
                );
            }
        } catch (error) {
            console.error('Error deleting avatar:', error);
            enqueueSnackbar(t('profile.avatar.error.delete') || 'Ошибка удаления аватара', { variant: 'error' });
        } finally {
            setIsLoadingAvatar(false);
        }
    };

    const handleAvatarClick = () => {
        fileInputRef.current?.click();
    };

    const handleAvatarError = () => {
        setAvatarError(true);
    };

    const handleProfessionChange = async (value: string | number) => {
        const newProfession = String(value);
        if (newProfession === (user?.specialty || '')) return;
        setUpdatingProfession(true);
        try {
            const updatedUser = await updateCurrentUserProfile({ specialty: newProfession });
            if (updatedUser) {
                dispatch(setUser(updatedUser));
                enqueueSnackbar(t('profile.professionUpdated') || 'Специальность обновлена', { variant: 'success' });
            }
        } catch (err) {
            enqueueSnackbar(err instanceof Error ? err.message : 'Ошибка обновления профессии', { variant: 'error' });
        } finally {
            setUpdatingProfession(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="profile-dialog-overlay" onClick={onClose}>
            <div className="profile-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="profile-dialog-header">
                    <h3>{t('profile.title')}</h3>
                    <button className="profile-dialog-close" onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className="profile-dialog-content pt-0">
                    <div className='d-flex flex-row'>
                        <div className="profile-avatar d-flex flex-column align-items-center justify-content-between me-3 w-50">
                            <span></span>
                            <div className="d-flex flex-column align-items-center justify-content-center gap-3">
                                <div
                                    className={`avatar-container ${isLoadingAvatar ? 'loading' : ''}`}
                                >
                                    {getAvatarFullUrl() && !avatarError ? (
                                        <img
                                            src={getAvatarFullUrl()!}
                                            alt="Avatar"
                                            className="avatar-image"
                                            onError={handleAvatarError}
                                        />
                                    ) : (
                                        <div className="avatar-placeholder">
                                            <span>👤</span>
                                        </div>
                                    )}
                                    {isLoadingAvatar && (
                                        <div className="avatar-loading-overlay">
                                            <div className="spinner"></div>
                                        </div>
                                    )}
                                    {!isLoadingAvatar && (
                                        <div className="avatar-hover-overlay">
                                            <button
                                                className="avatar-icon-btn upload-icon-btn"
                                                onClick={handleAvatarClick}
                                                title={t('profile.avatar.upload') || 'Загрузить аватар'}
                                            >
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M17 8L12 3L7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M12 3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                            {getAvatarFullUrl() && !avatarError && (
                                                <button
                                                    className="avatar-icon-btn delete-icon-btn"
                                                    onClick={handleAvatarDelete}
                                                    title={t('profile.avatar.delete') || 'Удалить аватар'}
                                                >
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleAvatarUpload}
                                    style={{ display: 'none' }}
                                />
                                <h4>{user?.first_name || '-'} {user?.last_name || '-'}</h4>
                            </div>
                            <div className="profile-actions m-0 p-0 w-100">
                                <button className="profile-action-btn logout-btn w-100" onClick={onLogout}>
                                    {t('profile.logout')}
                                </button>
                            </div>
                        </div>
                        <div className="profile-info d-flex flex-column w-100 gap-2">
                            <div className="profile-field">
                                <label>{t('profile.field.firstName')}</label>
                                <span>{user?.first_name || '-'}</span>
                            </div>
                            <div className="profile-field">
                                <label>{t('profile.field.lastName')}</label>
                                <span>{user?.last_name || '-'}</span>
                            </div>
                            <div className="profile-field">
                                <label>{t('profile.field.email')}</label>
                                <span>{user?.email || '-'}</span>
                            </div>
                            <div className="profile-field">
                                <label>{t('profile.field.role')}</label>
                                <span>{user?.role_name || '-'}</span>
                            </div>

                        </div>
                    </div>
                    <div className="profile-info mb-0 d-flex flex-column gap-2">
                        <div className="profile-field">
                            <label>{t('profile.field.profession')}</label>
                            {loadingProfessions ? (
                                <span className="text-muted">Загрузка...</span>
                            ) : (
                                <CustomSelect
                                    options={[
                                        { value: '', label: t('profile.professionEmpty') || 'Не выбрана' },
                                        ...(user?.specialty && !professions.includes(user.specialty)
                                            ? [{ value: user.specialty, label: user.specialty }]
                                            : []),
                                        ...professions.map((p) => ({ value: p, label: p })),
                                    ]}
                                    value={user?.specialty || ''}
                                    onChange={handleProfessionChange}
                                    placeholder={t('profile.professionEmpty') || 'Не выбрана'}
                                    className="profile-profession-select"
                                    disabled={updatingProfession}
                                    searchable
                                    searchPlaceholder={t('auth.searchSpecialty')}
                                />
                            )}
                            <p className="profile-field-hint mt-1 mb-0">{t('profile.professionHint')}</p>
                        </div>
                        <div className="profile-field">
                            <label>{t('profile.field.organization')}</label>
                            <span>{user?.organization || '-'}</span>
                        </div>
                        <div className="d-flex flex-row gap-3">
                            <div className="profile-field w-50">
                                <label>{t('profile.field.recognitionType')}</label>
                                <select className="input-secondary w-100 mt-2" value={currentType} onChange={handleTypeChange}>
                                    <option value="azure">{t('profile.recognition.option1')}</option>
                                    <option value="qwen">{t('profile.recognition.option2')}</option>
                                </select>
                            </div>
                            <div className="profile-field w-50">
                                <label>{t('profile.language.label')}</label>
                                <select
                                    className="input-secondary w-100 mt-2"
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value === 'ru' ? 'ru' : 'en')}
                                >
                                    <option value="ru">{t('profile.language.ru')}</option>
                                    <option value="en">{t('profile.language.en')}</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileDialog; 